/**
 * Structure-view tuning (`structures.json`, M3 plan R13), validated with Zod at load.
 *
 * - `poolPerKey`: how many released views of one (asset id, look) pair the free list keeps for reuse.
 * - `preloadPerKey`: views the `structures` enter hook instantiates ahead of time per (asset id, look) pair.
 * - `preloadLooks`: the look the enter hook assumes for relays, lane rings and POIs (stations carry their own). A
 *   guess that misses the session's look only costs a pool miss.
 * - `warmUp`: compile the preloaded views' pipelines (`renderer.compileAsync`) in the enter hook.
 */
import { z } from 'zod'
import { FACTION_IDS, type FactionId } from '../../render/contracts'
import structuresJson from './structures.json'

const factionId = z.enum(FACTION_IDS as unknown as [FactionId, ...FactionId[]])

export const structuresConfigSchema = z
  .object({
    poolPerKey: z.number().int().min(0).max(64),
    preloadPerKey: z.number().int().min(0).max(8),
    preloadLooks: z.object({ relay: factionId, laneRing: factionId, poi: factionId }).strict(),
    warmUp: z.boolean(),
  })
  .strict()

export type StructuresConfig = z.infer<typeof structuresConfigSchema>

export function loadStructuresConfig(raw: unknown = structuresJson): StructuresConfig {
  return structuresConfigSchema.parse(raw)
}
